import * as React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { DemoContainer, DemoItem } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { Typography, IconButton, MenuItem, Select, SelectChangeEvent, Button } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ClearIcon from '@mui/icons-material/Clear';

interface CustomCalendarHeaderProps {
  currentMonth: Dayjs;
  onMonthChange: (newMonth: Dayjs) => void;
  onYearChange: (newYear: number) => void;
  onClear?: () => void;
}

let convertDateFormat = (dateString: any) => {
  if (!dateString) return null;
  const parts = dateString.split('-');
  if (parts.length !== 3) {
    throw new Error('Invalid date format. Expected DD-MM-YYYY');
  }
  const [day, month, year] = parts;
  return `${month}-${day}-${year}`;
}

const CustomCalendarHeader: React.FC<CustomCalendarHeaderProps> = (props) => {
  const { currentMonth, onMonthChange, onYearChange, onClear } = props;

  const handlePreviousMonth = () => {
    onMonthChange(currentMonth.subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    onMonthChange(currentMonth.add(1, 'month'));
  };

  const handleYearChange = (event: SelectChangeEvent<number>) => {
    event.stopPropagation();
    const newYear = event.target.value as number;
    onYearChange(newYear);
  };

  const years = Array.from({ length: 31 }, (_, i) => 2000 + i);

  return (
    <div>
      <div style={{ borderTop: '1px solid #ccc', marginBottom: '16px' }}></div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', padding: '0 8px' }}>
        <IconButton onClick={handlePreviousMonth}>
          <ArrowBackIosIcon />
        </IconButton>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography variant="h6" style={{ margin: 0 }}>
            {currentMonth.format('MMMM')}
          </Typography>
          <Select
            value={currentMonth.year()}
            onChange={handleYearChange}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            variant="outlined"
            size="small"
            sx={{
              margin: 0,
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
          >
            {years.map((year) => (
              <MenuItem
                key={year}
                value={year}
                onClick={(e) => e.stopPropagation()}
              >
                {year}
              </MenuItem>
            ))}
          </Select>
        </div>
        <div>
          <IconButton onClick={handleNextMonth}>
            <ArrowForwardIosIcon />
          </IconButton>
          {onClear && (
            <IconButton onClick={onClear} sx={{ ml: 1 }}>
              <ClearIcon />
            </IconButton>
          )}
        </div>
      </div>
    </div>
  );
};

interface DateCalendarValueProps {
  onDateSelect: (date: Dayjs | null) => void;
  onClose?: () => void;
  dateValue: string | null;
}

const DateCalendarValue: React.FC<DateCalendarValueProps> = ({ onDateSelect, onClose, dateValue }) => {
  const [value, setValue] = React.useState<Dayjs | null>(dateValue ? dayjs(convertDateFormat(dateValue)) : null);
  const calendarRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setValue(dateValue ? dayjs(convertDateFormat(dateValue)) : null);
  }, [dateValue]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const dateIcons = document.querySelectorAll('.icon-date');
      const clickedOnIcon = Array.from(dateIcons).some(icon =>
        icon.contains(event.target as Node)
      );

      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node) &&
        !clickedOnIcon
      ) {
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleDateChange = (newValue: Dayjs | null) => {
    setValue(newValue);
    onDateSelect(newValue);
  };

  const handleMonthChange = (newMonth: Dayjs) => {
    const newDate = value ? value.month(newMonth.month()).year(newMonth.year()) : newMonth;
    setValue(newDate);
  };

  const handleYearChange = (newYear: number) => {
    const newDate = value ? value.year(newYear) : dayjs().year(newYear);
    setValue(newDate);
  };

  const handleClear = () => {
    setValue(null);
    onDateSelect(null);
  };

  return (
    <div className="milstone_date_pick" ref={calendarRef}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DemoContainer components={['DateCalendar', 'DateCalendar']}>
          <div>
            <DemoItem label={value ? value.format('DD-MM-YYYY') : 'No date selected'} sx={{ backgroundColor: "#fff", border: "1px solid #2a3fbf", borderRadius: "20px", width: "275px", P: { px: 2, pt: 1, color: "#333" } }}>
              <DateCalendar
                sx={{ width: "100%", h6: { fontSize: "14px", color: "#333333" }, svg: { width: "18px" }, button: { p: 0, borderRadius: 0, }, m: 3 }}
                value={value}
                onChange={handleDateChange}
                slots={{
                  // @ts-ignore
                  calendarHeader: CustomCalendarHeader,
                }}
                slotProps={{
                  calendarHeader: {
                    currentMonth: value || dayjs(),
                    onMonthChange: handleMonthChange,
                    // @ts-ignore
                    onYearChange: handleYearChange,
                    onClear: handleClear,
                  },
                }}
              />
            </DemoItem>
          </div>
        </DemoContainer>
      </LocalizationProvider>
    </div>
  );
};

export default DateCalendarValue;